import { cloneDeep, get, map } from "lodash";
import moment from "moment";
import "./dayView.scss";
import { createRef, useEffect, useMemo, useRef, useState } from "react";
import { REMINDER_FUTURE_DATED_NO_ACTION, REMINDER_NO_ACTION, REMINDER_STATUS_COLOR_CODE, REMINDER_STATUS_COLOR_CODE_FOR_BACKGROUND } from "@utils/constants";
import { ReminderTooltip } from "../reminderTooltip";
import { useRemindersContext } from "@components/drawerCalendar/useReminders";
import Icon from "@assets/icon_config";
import { AllDayCadences } from "@components/drawerOutreaches/Cadence";
import { DAY } from "@components/drawerCalendar/constant";

export const DayView = ({ members, updateMembers, updateStatus }: any) => {
  const { dayReminders, setDayReminders, selectDate, groupRemindersByDates, editReminderHandler, getTimeZoneInfo } = useRemindersContext();

  const reminderList: any = useMemo(() => {
    const dayReminderList: any = Object.values(dayReminders)[0] || [];
    return dayReminderList?.sort((a: any, b: any) => {
      const dateA = moment(a.reminderStartDate);
      const dateB = moment(b.reminderEndDate);
      return dateA.diff(dateB); // Compare in descending order
    });
  }, [dayReminders]);

  const scrollContainerRef = useRef(null);
  const dropPoint = useRef(0);
  const currentTimeLine = createRef<any>();

  useEffect(() => {
    console.log("DayView.useEffect1");
    const scrollContainer: any = scrollContainerRef.current;
    if (scrollContainer) {
      scrollContainer.addEventListener("scroll", () => {
        dropPoint.current = scrollContainer.scrollTop;
      });
    }
    return () => {
      if (scrollContainer) {
        scrollContainer.removeEventListener("scroll", () => {
          dropPoint.current = scrollContainer.scrollTop;
        });
      }
    };
  }, []);

  useEffect(() => {
    console.log("DayView.useEffect2");
    currentTimeLine?.current?.scrollIntoView({
      block: "center",
      behavior: "smooth",
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dayReminders]);

  const drop = (e: any) => {
    e.preventDefault();
    const draggedReminder = get(JSON.parse(e.dataTransfer.getData("reminder")), "reminder");
    if (!draggedReminder?.reminderStatus || draggedReminder?.reminderStatus === REMINDER_NO_ACTION) {
      const yCoordinate = e.clientY;
      const reminderStart = moment.utc(draggedReminder?.reminderStartDate).local();
      const reminderEnd = moment.utc(draggedReminder?.reminderEndDate).local();
      const time = ((dropPoint.current + yCoordinate - 118) / 120) * 100;
      const hour = Math.trunc(time / 100);
      const minutes = Math.trunc(((time % 100) / 120) * 60);
      const reminderStartDateTime = moment(`${reminderStart.format("YYYY-MM-DD")}`);
      reminderStartDateTime.add(hour, "hours");
      reminderStartDateTime.add(minutes <= 20 ? 0 : 30, "minutes");
      const differenceInMinutes = reminderEnd.diff(reminderStart, "minutes");
      const reminderEndDateTime = reminderStartDateTime.clone().add(differenceInMinutes, "minutes");
      editReminderHandler(
        { ...draggedReminder, reminderStartDate: reminderStartDateTime, reminderEndDate: reminderEndDateTime },
        () => {
          const updatedReminderList = [...reminderList].map((item: any) => {
            let tempItem = cloneDeep(item);
            if (draggedReminder.reminderId === tempItem.reminderId) {
              tempItem = {
                ...tempItem,
                reminderStartDate: reminderStartDateTime,
                reminderEndDate: reminderEndDateTime,
              };
            }
            return tempItem;
          });
          setDayReminders(groupRemindersByDates(updatedReminderList));
        },
        members,
        updateMembers,
        updateStatus
      );
    }
  };

  // Function to stop drop event propagation on reminder card
  const dragOver = (e: any) => {
    e.preventDefault();
  };

  // Function to handle when dragStarts
  const handleDragStart = (event: any, reminder: any) => {
    event.dataTransfer.setData("reminder", JSON.stringify({ reminder }));
  };

  const getCurrentTimePosition = () => {
    const date = moment();
    const hr = date.hour();
    const min = date.minute();
    return hr * 120 + min * 2;
  };

  return (
    <div className="ui-healthEm-day-view" ref={scrollContainerRef}>
      <div className="ui-healthEm-day-view__header">
        <div className="ui-healthEm-day-view__header__filler-container"></div>
        <div className="ui-healthEm-day-view__header__day-date">
          <div className="ui-healthEm-day-view__header__day-date__day">{selectDate.format("dddd").toUpperCase()}</div>
          <div className="ui-healthEm-day-view__header__day-date__date">{selectDate.format("Do MMMM YYYY")}</div>
        </div>
        <div className="ui-healthEm-day-view__header__filler-container ui-healthEm-week-view__header__filler-container__timeZone">{getTimeZoneInfo()}</div>
        
        <AllDayCadences view={DAY} date={selectDate} />
      </div>
      <div className="ui-healthEm-day-view__content">
        <div className="ui-healthEm-day-view__content__layout">
          {map([12, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11], (item: number) => {
            return <TimeSlots item={item} slot="AM" />;
          })}
          {map([12, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11], (item: number) => {
            return <TimeSlots item={item} slot="PM" />;
          })}
        </div>
        <div className="ui-healthEm-day-view__content__reminders-container" onDrop={drop} onDragOver={dragOver}>
          {reminderList?.map((reminder: any) => (
            <Reminder reminder={reminder} reminderList={reminderList} handleDragStart={handleDragStart} />
          ))}
        </div>
        {moment(selectDate).format("YYYY-MM-DD") === moment().format("YYYY-MM-DD") && (
          <div className="ui-healthEm-day-view__content__current-time-line-container">
            <div
              className="ui-healthEm-day-view__content__current-time-line-container__current-time-line"
              style={{
                transform: `translateY(${getCurrentTimePosition()}px)`,
              }}
              ref={currentTimeLine}
            ></div>
          </div>
        )}
      </div>
    </div>
  );
};

const TimeSlots = (props: { item: {} | null | undefined; slot: {} | null | undefined }) => {
  return (
    <div className="ui-healthEm-day-view__content__layout__time-slot">
      <div className="ui-healthEm-day-view__content__layout__time-slot__slot ui-healthEm-day-view__content__layout__time-slot__slot--left">
        {props.item} {props.slot}
      </div>
      <div className="ui-healthEm-day-view__content__layout__time-slot__section">
        <div className="ui-healthEm-day-view__content__layout__time-slot__section--top"></div>
        <div className="ui-healthEm-day-view__content__layout__time-slot__section--bottom"></div>
      </div>
      <div className="ui-healthEm-day-view__content__layout__time-slot__slot ui-healthEm-day-view__content__layout__time-slot__slot--right">
        {props.item} {props.slot}
      </div>
    </div>
  );
};

const Reminder = ({ reminder, reminderList, handleDragStart }: any) => {
  const [onDragStart, setOnDragStart] = useState(false);
  const { reminderId, patientName, reminder: description, reminderStartDate, recurrence , reminderEndDate, reminderStatus, reminderTitle } = reminder || {};

  const getStartPosition = (from: any) => {
    const date = moment.utc(from).local();
    const hr = date.hour();
    const min = date.minute();
    return hr * 120 + min * 2;
  };

  const getReminderDurationHeight = (from: any, to: any) => {
    const fromDate = moment(from);
    const toDate = moment(to);
    return toDate.diff(fromDate, "minutes") * 2;
  };

  const getReminderWidth = (from: any, to: any, reminderId: any) => {
    let count = [...reminderList]?.filter((rem: any) => {
      return (
        moment.utc(from).format("YYYY-MM-DD HH:mm:ss") <= moment.utc(rem.reminderEndDate).format("YYYY-MM-DD HH:mm:ss") &&
        moment.utc(to).format("YYYY-MM-DD HH:mm:ss") >= moment.utc(rem.reminderStartDate).format("YYYY-MM-DD HH:mm:ss") &&
        reminderId !== rem.reminderId
      );
    }).length;
    return count > 0 ? 100 / (count + 1) : 100;
  };

  const getReminderLeft = (from: any, to: any, reminderId: any) => {
    const w = getReminderWidth(from, to, reminderId);
    const overlappedRem = [...reminderList].filter((rem: any) => {
      return (
        moment.utc(from).format("YYYY-MM-DD HH:mm:ss") <= moment.utc(rem.reminderEndDate).format("YYYY-MM-DD HH:mm:ss") &&
        moment.utc(to).format("YYYY-MM-DD HH:mm:ss") >= moment.utc(rem.reminderStartDate).format("YYYY-MM-DD HH:mm:ss")
      );
    });
    return w === 100 ? 0 : w * overlappedRem.findIndex((rem) => rem.reminderId === reminderId);
  };

  return (
    <ReminderTooltip reminder={reminder} onDragStart={onDragStart} placement={"top"}>
      <div
        className={`ui-healthEm-day-view__content__reminders-container__reminder ${
          !reminder?.reminderStatus || reminder?.reminderStatus === REMINDER_NO_ACTION ? "" : "ui-healthEm-day-view__content__reminders-container__reminder__disable-drag"
        }`}
        style={{
          transform: `translateY(${getStartPosition(reminderStartDate)}px)`,
          height: getReminderDurationHeight(reminderStartDate, reminderEndDate),
          background: `${
            REMINDER_STATUS_COLOR_CODE_FOR_BACKGROUND[reminderStatus ? reminderStatus : moment.utc(reminderEndDate).local() < moment() ? REMINDER_NO_ACTION : REMINDER_FUTURE_DATED_NO_ACTION]
          }`,
          borderLeft: `4px solid ${
            REMINDER_STATUS_COLOR_CODE[reminderStatus ? reminderStatus : moment.utc(reminderEndDate).local() < moment() ? REMINDER_NO_ACTION : REMINDER_FUTURE_DATED_NO_ACTION]
          }`,
          width: `${getReminderWidth(reminderStartDate, reminderEndDate, reminderId)}%`,
          left: `${getReminderLeft(reminderStartDate, reminderEndDate, reminderId)}%`,
        }}
        draggable={!reminder?.reminderStatus || reminder?.reminderStatus === REMINDER_NO_ACTION}
        onDragStart={(event) => {
          setOnDragStart(true);
          handleDragStart(event, reminder);
        }}
        onDragEnd={() => setOnDragStart(false)}
      >
        {patientName && (
          <div className="ui-healthEm-day-view__content__reminders-container__reminder__patient-name">
            {patientName}
            {recurrence && (
              <span className="ui-healthEm-day-view__content__reminders-container__reminder__recurring-reminder-icon">
                <Icon name="refreshFilled" />
              </span>
            )}
          </div>
        )}
        <div className={`ui-healthEm-day-view__content__reminders-container__reminder__reminder-description ${!patientName ? "f18 w500" : ""}`}>
          {reminderTitle}
          {!patientName && recurrence && (
            <span className="ui-healthEm-day-view__content__reminders-container__reminder__recurring-reminder-icon">
              <Icon name="refreshFilled" />
            </span>
          )}
        </div>
      </div>
    </ReminderTooltip>
  );
};

export default DayView;
